import { fetchInstance } from "@/utils/fetchInstance";
import { JwtCallbackResponse } from "@/models/auth/Jwt";
import { getDeviceId } from "@/utils/deviceId";

// Returned in the `data` of an OTP request response. On a 429 the backend tells
// us how long to wait before allowing another send.
export interface OtpRequestData {
  retry_after?: number;
}

// Returned in the `data` of a failed verify (wrong code) — how many tries are
// left before the code is burned.
export interface OtpVerifyFailureData {
  remaining_attempts?: number;
}

// Phone-based OTP login/registration for the web app. See docs/web-otp-auth-v2.md.
// These endpoints are vitrin-scoped: do NOT send a Miniapp-UUID header.
export class AuthService {
  // Step 1 — SMS a one-time code to the phone. Always responds generically
  // (never reveals whether the phone has an account).
  static async requestOtp(phone: string) {
    return fetchInstance<OtpRequestData | null, { phone: string }>(
      "auth/otp/request",
      {
        method: "POST",
        body: { phone },
      },
    );
  }

  // Step 2 — exchange the code for a session (tokens + user). Same shape as the
  // Eitaa JWT callback response. A stable `device_id` is baked into the issued
  // JWT for the course device-limit check (see docs/web-otp-auth-v2.md §3).
  static async verifyOtp(phone: string, code: string) {
    const device_id = getDeviceId() ?? undefined;
    return fetchInstance<
      JwtCallbackResponse,
      { phone: string; code: string; device_id?: string }
    >("auth/otp/verify", {
      method: "POST",
      body: { phone, code, device_id },
    });
  }

  // Revoke the whole session family. Caller clears local tokens afterward.
  static async logout(refreshToken: string) {
    return fetchInstance<null, { refresh_token: string }>("auth/jwt/logout", {
      method: "DELETE",
      body: { refresh_token: refreshToken },
      auth: true,
    });
  }
}
